import { OverlayContainer } from '@angular/cdk/overlay';
import { OnDestroy, Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { Subject } from 'rxjs';
/**
 * Interface para definição
 * de steps do material Stepper
 */
export interface StepType {
  label: string;
  fields: FormlyFieldConfig;
}

/**
 * Template padrão de Material Stepper
 * utilizando Formly
 */
export const HTML_TEMPLATE_STEP = `
    <form style="height: 100%" [formGroup]="form" #formBaseForm (ngSubmit)="submit()" [ngClass]="{'step-display-none' : !stepBtnsVisible && !stepLabelVisible}">
    <mat-horizontal-stepper #stepper>
        <mat-step [stepControl]="form" *ngFor="let step of steps; let index=index; let last=last;">
            <div *ngIf="stepLabelVisible">
              <ng-template matStepLabel>{{ step.label }}</ng-template>
            </div>
            <formly-form
                [form]="form.at(index)"
                [(model)]="model"
                [fields]="step.fields"
                [options]="options[index]">
            </formly-form>
            <div style="margin-top: 20px"></div>
            <div fxLayout="row" fxFill>
              <div fxFlex="5" fxLayoutAlign="space-between center" *ngIf="stepBtnsVisible">
                <a mat-flat-button matTooltip="Anterior" matTooltipPosition="after"
                    [disabled]="index === 0"
                    (click)="stepper.previous()"
                    matStepperPrevious>
                    <mat-icon>chevron_left</mat-icon>
                </a>
                <a mat-flat-button matTooltip="Próximo" matTooltipPosition="after"
                    matStepperNext
                    (click)="stepper.next()"
                    [disabled]="last || form.at(index).invalid">
                    <mat-icon>chevron_right</mat-icon>
                </a>
              </div>
              <div fxFlex fxLayoutAlign="start center" *ngIf="!footerVisible">
                <button mat-flat-button (click)="submit()" [mat-dialog-close]="model"
                  [disabled]="form.at(index).invalid || loading">
                    <mat-icon>done</mat-icon>
                </button>
                &nbsp;&nbsp;
                <button mat-stroked-button color="warn" [mat-dialog-close]="model">
                    <mat-icon>clear</mat-icon>
                </button>
                &nbsp;&nbsp;<i *ngIf="loading" class="fa fa-spinner fa-pulse fa-fw fa-2x"></i>
              </div>
            </div>
        </mat-step>
      </mat-horizontal-stepper>
    </form>
    <br/>
    <br/>
    <!--{{ model | json}}-->`;

/**
 * Template padrão de Material Stepper
 * utilizando Formly
 */
export const HTML_TEMPLATE = `
<form [formGroup]="form">
  <formly-form [model]="model" [fields]="fields" [options]="options" [form]="form.at(0)">
  </formly-form>
  <div style="margin-top: 20px"></div>
  <div fxLayout="row" fxFill>
    <div fxFlex="5" fxLayoutAlign="space-between center" *ngIf="stepBtnsVisible">
      <a mat-flat-button matTooltip="Anterior" matTooltipPosition="after"
          [disabled]="index === 0"
          (click)="stepper.previous()"
          matStepperPrevious>
          <mat-icon>chevron_left</mat-icon>
      </a>
      <a mat-flat-button matTooltip="Próximo" matTooltipPosition="after"
          matStepperNext
          (click)="stepper.next()"
          [disabled]="last || form.invalid">
          <mat-icon>chevron_right</mat-icon>
      </a>
    </div>
    <div fxFlex fxLayoutAlign="start center">
      <button mat-flat-button (click)="submit()" [mat-dialog-close]="model"
        [disabled]="form.invalid || loading">
          <mat-icon>done</mat-icon>
      </button>
      &nbsp;&nbsp;
      <button mat-stroked-button color="warn" [mat-dialog-close]="model">
          <mat-icon>clear</mat-icon>
      </button>
      &nbsp;&nbsp;<i *ngIf="loading" class="fa fa-spinner fa-pulse fa-fw fa-2x"></i>
    </div>
  </div>
</form>
<!--{{ model | json}}-->
`;

@Injectable({
  providedIn: 'root',
})
export class BaseFormComponent<T> implements OnDestroy {
  /**
   * Define se os labels
   * do Step ficarão visíveis ou não
   * Default: true
   * SET
   */
  public stepLabelVisible = true;
  public stepBtnsVisible = true;
  public footerVisible = true;
  public cancelarVisible = true;
  public loading = false;
  /**
   * Etapas do Formulário
   */
  public steps: StepType[] = [
    { label: 'Step 01', fields: [] },
    { label: 'Step 02', fields: [] },
  ] as StepType[];

  /**
   * Configurações de FormGroup
   * conforme as etapas criadas
   */
  public form: FormArray | any;

  public fields: FormlyFieldConfig[];

  /**
   * Opções do Formly
   */
  public options: FormlyFormOptions | FormlyFormOptions[] = {};

  /**
   * Define se o formulário
   * será linear ou não
   */
  public linear: boolean;

  /**
   * Model que será utilizado
   * Para passar e retornar dados
   */
  public model: T;

  public onDestroy$ = new Subject<void>();

  /**
   * Atribui valores Default
   */
  constructor() {
    this.model = {} as T | any;
    this.linear = false;
    this.steps = [{ label: 'Step 01', fields: [] }] as StepType[];
    this.afterConstructor();
  }

  toggleTheme(overlayContainer: OverlayContainer): void {
    if (overlayContainer.getContainerElement().classList.contains('dark-theme')) {
      overlayContainer.getContainerElement().classList.remove('dark-theme');
      overlayContainer.getContainerElement().classList.add('light-theme');
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.unsubscribe();
  }
  /**
   * para ser executado nas classes filhas
   * após o construtor para atribuir
   * os valores do Form group e Options do Formly
   */
  public afterConstructor(): void {
    this.form = new FormArray(this.steps.map(() => new FormGroup({})));
    this.options = this.steps.map<FormlyFormOptions>(() => ({} as FormlyFormOptions));
  }

  /**
   * Realiza filtro em listas
   * por um determinada propriedade
   * - remove acentuação
   * - transforma em minúsculo
   *
   * @param list lista de objetos
   * @param value valor a ser filtrado
   * @param prop item do array que será pesquisado
   * @example  filter({ "nome": "João", "idade": 54 }, 'Joao', 'Nome')
   */
  public filter(list: any[], value: string, prop: string): any[] {
    return list.filter(
      (item) => this.removeAcentuacao(item[prop]).search(this.removeAcentuacao(value)) >= 0,
    );
  }

  //#region removeAcentuacao

  /*
   * @param  texto string acentuada
   * @return string sem acento
   */
  public removeAcentuacao(
    value: string,
    lowercase: boolean = true,
    clearNotWord: boolean = true,
  ): string {
    let texto = typeof value === 'string' ? value : undefined;
    if (texto) {
      texto = value.replace(/^\s+|\s+$/g, '');

      const mapaAcentosHex: { [key: string]: RegExp } = {
        a: /[\xE0-\xE6]/g,
        A: /[\xC0-\xC6]/g,
        e: /[\xE8-\xEB]/g,
        E: /[\xC8-\xCB]/g,
        i: /[\xEC-\xEF]/g,
        I: /[\xCC-\xCF]/g,
        o: /[\xF2-\xF6]/g,
        O: /[\xD2-\xD6]/g,
        u: /[\xF9-\xFC]/g,
        U: /[\xD9-\xDC]/g,
        c: /\xE7/g,
        C: /\xC7/g,
        n: /\xF1/g,
        N: /\xD1/g,
      };

      // loop para detectar caracteres com acento e substituir
      // por sem acento. EX: ç > c, é > e, etc
      for (const letra in mapaAcentosHex) {
        if (letra) {
          const expressaoRegular: RegExp = mapaAcentosHex[letra];
          texto = texto.replace(expressaoRegular, letra);
        }
      }
      // removendo todos os caracteres especiais /+-*&¨%$# e etc
      if (clearNotWord) {
        texto = texto.replace(/[^\w+]|\W/g, '');
      }
      // converte em minusculas
      if (lowercase) {
        texto = texto.toLowerCase();
      }
    }
    return texto || value;
  }

  //#endregion RemoveAcentuação
}
